import {
    h, ComponentType, JSX, Ref, ComponentChildren
} from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import classname from "../../../foundation/utils/classname";
import { useDisableAutoFillSafariBrowsers } from "../../hooks/useDisableAutoFillSafariBrowsers";
import IconProps from "../../Icons/IconProps";

import styles from "./Input.module.css";

type NativeInputProps = Omit<JSX.HTMLAttributes<HTMLInputElement>, "icon">;

enum Variant {
    Default = "default",
    Ninja = "ninja"
}

interface Props extends NativeInputProps {
    inputClass?: string;
    textClass?: string; /* Class of the text style. Example: "body-1", "caption-1 medium"*/
    variant?: Variant;
    icon?: ComponentType<IconProps>;
    ellipsis?: boolean;
    showBackground?: boolean;
    inputRef?: Ref<HTMLInputElement>;
    mirrorContent?: ComponentChildren;
    shouldSelectAllOnAutoFocus?: boolean;
    onFocus?: (ev: Event) => void;
}

const OVERFLOW_THRESHOLD = 1; // px

// TODO-electron: Separate readOnly and disabled states
function Input({
    class: cssClass,
    inputClass,
    textClass = "body-1",
    style,
    value,
    placeholder,
    variant = Variant.Default,
    icon: Icon,
    autoFocus,
    disabled,
    ellipsis,
    showBackground = false,
    inputRef,
    mirrorContent,
    shouldSelectAllOnAutoFocus,
    onFocus,
    ...nativeInputProps
}: Props) {
    const [overflowed, setOverflowed] = useState(false);
    const ref = useRef<HTMLInputElement | null>(null);
    const hasValue = !!value;

    if (ref.current) {
        useDisableAutoFillSafariBrowsers(ref.current as unknown as HTMLInputElement);
    }

    useEffect(() => {
        if (autoFocus) {
            ref.current?.focus();

            if (shouldSelectAllOnAutoFocus) {
                handleSelectAll();
            }
        }
    }, [autoFocus]);

    useEffect(() => {
        if (!ellipsis || !value || !ref.current) {
            return;
        }

        // This is a hacky way to detect if the input is overflowing.
        // It's useful when we want to disable horizontal scrolling when the input is ellipsized.
        const { offsetWidth, scrollWidth } = ref.current;
        const isOverflowing = scrollWidth - offsetWidth > OVERFLOW_THRESHOLD;

        setOverflowed(isOverflowing);
    }, [value, ellipsis]);

    function handleRef(el: HTMLInputElement | null) {
        ref.current = el;

        if (inputRef) {
            if (typeof inputRef === "function") {
                inputRef(el);
            } else {
                inputRef.current = el;
            }
        }
    }

    function handleSelectAll() {
        if (ref.current && ref.current.type === "text") {
            ref.current.setSelectionRange(0, (value as string).length, "backward");
            ref.current.scrollLeft = 0;
        }
    }

    function handleFocus(ev: Event) {
        // This is added to have a consistent selection behavior when ellipsis is enabled.
        if (ellipsis) {
            handleSelectAll();
        }

        onFocus?.(ev);
    }

    function renderMirror() {
        const mirror = mirrorContent ?? (
            <span class={styles.mirror}>
                {value || placeholder || " "}
            </span>
        );

        return (
            <span class={styles.mirrorWrapper}>
                {mirror}
            </span>
        );
    }

    return (
        <label
            class={classname(textClass, styles.inputWrapper, cssClass, {
                [styles.ninja]: variant === Variant.Ninja,
                [styles.hasValue]: hasValue,
                [styles.hasIcon]: !!Icon,
                [styles.disabled]: disabled,
                [styles.overflowed]: overflowed,
                [styles.showBackground]: showBackground
            })}
            style={style}
            title={ellipsis ? value?.toString() : undefined}>
            {Icon && (
                <div class={styles.icon}>
                    <Icon/>
                </div>
            )}
            <input
                class={classname(styles.input, inputClass, {
                    [styles.ellipsis]: ellipsis
                })}
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                name="dummy_name_to_prevent_autofill" // prevents safari auto fill
                autoCorrect="off"
                autoComplete="off"
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                autoCapitalize="off"
                ref={handleRef}
                onFocus={handleFocus}
                {...nativeInputProps}>
            </input>
            {renderMirror()}
        </label>
    );
}

export default Input;
export {
    Props as InputProps,
    Variant as InputVariant
};
